'use client';

import { Flex, Icon, Loading } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import { useCopy } from '@/_hooks/useCopy';
import styles from './Pill.module.css';
import { forwardRef, useState, type MouseEvent } from 'react';
import type { IconSize } from '@/_types/scale';
import type { PillProps, PillIconSize } from './Pill.types';

const PILL_ICON_SIZE: Record<PillIconSize, IconSize> = {
  large: 16,
  normal: 14,
  small: 12,
};

export const Pill = forwardRef<HTMLDivElement, PillProps>(
  (
    {
      allowCopy,
      className,
      hasHover,
      iconLeft,
      iconRight,
      intent = 'default',
      internalIconWidth,
      isDark,
      isEllipsis,
      isIconOnly,
      isLoading,
      isRound,
      label,
      onClick,
      onLeftClick,
      onRightClick,
      size = 'normal',
    },
    ref,
  ) => {
    const { copyTextToClipboard } = useCopy();
    const [copied, setCopied] = useState<boolean>(false);

    const pillClasses: string = getClasses(styles.pill, className, {
      [styles.pillRounded]: !isRound,
      [styles.pillRound]: isRound,
      [styles.pillIconOnly]: isIconOnly,
      [styles.pillEllipsis]: isEllipsis,
      [styles.pillNormal]: size === 'normal',
      [styles.pillSmall]: size === 'small',
      [styles.pillLarge]: size === 'large',
      [styles.pillDefault]: intent === 'default',
      [styles.pillSecondary]: intent === 'secondary',
      [styles.pillAccent]: intent === 'accent',
      [styles.pillSuccess]: intent === 'success',
      [styles.pillHover]: hasHover,
      [styles.pillInfo]: intent === 'info',
      [styles.pillCaution]: intent === 'warning',
      [styles.pillDanger]: intent === 'danger',
      [styles.pillDark]: isDark,
      [styles.pillCopyable]: allowCopy,
    });

    const onPillClick = (event: MouseEvent<HTMLElement>) => {
      if (allowCopy) {
        setCopied(true);
        copyTextToClipboard(label.toString());

        setTimeout(() => {
          setCopied(false);
        }, 1000);
      }

      return onClick?.(event);
    };

    return (
      <Flex
        className={pillClasses}
        onClick={onClick || allowCopy ? onPillClick : undefined}
        ref={ref}
      >
        {isLoading && <Loading size={8} />}
        {iconLeft && (
          <Icon
            name={iconLeft}
            internalIconWidth={internalIconWidth}
            size={PILL_ICON_SIZE[size]}
            onClick={onLeftClick}
          />
        )}
        {!isIconOnly && (
          <span className={isEllipsis ? styles.pillEllipsis : undefined}>
            {copied ? 'Copied' : label}
          </span>
        )}
        {iconRight && (
          <Icon
            name={iconRight}
            internalIconWidth={internalIconWidth}
            size={PILL_ICON_SIZE[size]}
            onClick={onRightClick}
          />
        )}
      </Flex>
    );
  },
);

Pill.displayName = 'Pill';
